import clsx from "clsx";
import { isPartnerPackagePurchase } from "common";
import { sortBy } from "lodash";
import React from "react";
import { Product, ProductReference, Purchase, PurchaseItem } from "types";
import { ImageSizes, ProductImage } from "../Common/ProductImage";
import { useResource } from "@/hooks/useResource";

interface Logo {
    icon: JSX.Element;
    className?: string;
    style?: Record<string, unknown>;
}

interface PurchaseProductImageProps {
    purchase: Purchase;
    size?: keyof typeof ImageSizes;
    logo?: Logo;
}

/**
 * This will show the image of all products in the purchase. If there is more than one it will do the following:
 * 1. Use grid layout (instead of absolute/relative) to stack all the images behind the first one
 * 2. Offset the images using `left` prop to move them to the right
 * 3. Use zIndex to set the right order
 * 4. Set a white border to show the presence of all images
 */

export const PurchaseProductImage = ({ purchase, size = "small", logo }: PurchaseProductImageProps) => {
    const roundedElementsZIndex = 30; // values over 30 in some cases will be displayed on top of the dialog overlay (e.g. grayed out around modals)
    const roundedElementsClassName = `relative z-${roundedElementsZIndex}`;
    let zIndex = roundedElementsZIndex - 1; // to display counter/logo over the product image

    const parentProduct = isPartnerPackagePurchase(purchase) ? undefined : purchase.items[0].parentProduct; // all items in the purchase would have the same parentProduct

    const items = parentProduct
        ? [purchase.items[0]] // if the purchase have parent product just pick the first item to show the parent product image
        : sortBy(purchase.items.slice(0, 3), "object").reverse(); // Only show a max of 3 tiles even if there's more. Sorted to avoid display gift banner in the mist purchase level

    const itemsCount = purchase.items.length;

    return (
        <>
            {itemsCount > 1 && (
                <span className={roundedElementsClassName}>
                    <RoundedCounter count={itemsCount} />
                </span>
            )}
            {logo && (
                <span className={roundedElementsClassName}>
                    <RoundedLogo logo={logo} />
                </span>
            )}
            {/* Grid below required for MIST product image */}
            <div className={clsx("grid shrink-0", items.length > 1 ? "pr-0" : "pr-0")}>
                {items.map((item, index) => {
                    zIndex -= index;
                    return (
                        <ItemProductImage
                            key={item.id}
                            item={item}
                            size={size}
                            product={parentProduct}
                            style={{
                                left: items.length > 1 && `${index * 5}px`, // Move the image to the right to get a staggered effect
                                zIndex, // Move the last experience image to the bottom
                                // The next two are to stack the images in the same location
                                gridRowStart: 1,
                                gridColumnStart: 1,
                            }}
                            className="relative mr-1 w-full xl:mr-3"
                            classNames={{ image: "border-white w-6 h-6 xl:w-10 xl:h-10" }}
                        />
                    );
                })}
            </div>
        </>
    );
};

interface ItemProductImageProps {
    item: PurchaseItem;
    style?: any;
    product?: Product | ProductReference;
    className: string;
    classNames: { image?: string };
    size: keyof typeof ImageSizes;
}

export const ItemProductImage = ({ item, product, size, style, className, classNames }: ItemProductImageProps) => {
    // This can be an experience or gift offer so we must fetch it from the products collection
    // because the `item.product` reference doesn't have an `object` type
    const { data: productData } = useResource<Product>(["products", product?.id ?? item.product?.id]);

    return (
        <ProductImage
            product={productData ?? item.product}
            object={item.object}
            size={size}
            className={className}
            classNames={classNames} // Non-standard size and margin, TODO: What todo?
            style={style}
        />
    );
};

/**
 * This is a better counter than our <Counter /> component because our Counter component has a flat bottom. This component
 * adds a fixed width and height instead of using padding to give it a proper round shape
 */

export const RoundedCounter = ({ count, className }: { count: number; className?: string }) => (
    <div
        className={clsx(
            "absolute -top-1 -left-1 z-50 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-sm font-semibold leading-none text-white",
            className,
        )}
    >
        {count}
    </div>
);

const RoundedLogo = ({ logo }: { logo: Logo }) => {
    return (
        <span style={logo.style} className={clsx("absolute top-8 -left-1 z-50 h-5 w-5 rounded-full", logo.className)}>
            {logo.icon}
        </span>
    );
};
